import { hasValue } from '@repo/utils';
import type { AxiosInstance } from 'axios';

import type { TCalculateCtcRequest, TCalculateCtcResponse } from '../../types/calculate-ctc';
import type { TFactTrajectoryWriteDto } from '../../types/fact-trajectory-write';
import type { TFactTrajectoryCreateDefaults, TTrajectory } from '../../types/trajectory';
import { throwApiError } from '../../utils/errors';
import { isMissingTrajectoryDraftError } from '../../utils/is-missing-trajectory-draft-error';

export class TrajectoriesApi {
  private readonly agent: AxiosInstance;

  constructor(agent: AxiosInstance) {
    this.agent = agent;
  }

  async fetchByWellbore(
    wellboreId: number,
    signal?: AbortSignal,
    draftId?: number | null,
    options?: { infoOnly?: boolean },
  ): Promise<TTrajectory[]> {
    try {
      const { data, status } = await this.agent.get<TTrajectory[]>(`trajectories/${wellboreId}`, {
        params: {
          ...(options?.infoOnly ? { infoOnly: true } : {}),
          ...(hasValue(draftId) ? { draftId } : {}),
        },
        signal,
      });

      // 204 / empty body: axios may yield `""` or `undefined`, not an array.
      if (status === 204 || !Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (e) {
      // TEMP workaround: treat "Trajectory draft data not found" as empty list until backend returns 204.
      if (isMissingTrajectoryDraftError(e)) {
        return [];
      }

      throwApiError(e);
    }
  }

  async upsertFactTrajectory(
    wellId: number,
    draftId: number,
    trajectory: TFactTrajectoryWriteDto,
    signal?: AbortSignal,
  ): Promise<TFactTrajectoryWriteDto> {
    try {
      const { data } = await this.agent.put<TFactTrajectoryWriteDto>(
        `wells/${wellId}/details/fact/trajectories`,
        trajectory,
        {
          params: { draftId },
          signal,
        },
      );

      return data ?? trajectory;
    } catch (e) {
      throwApiError(e);
    }
  }

  async deleteFactTrajectory(
    wellId: number,
    draftId: number,
    trajectoryDraftId: number,
    signal?: AbortSignal,
  ): Promise<void> {
    try {
      await this.agent.delete(`wells/${wellId}/details/fact/trajectories/${trajectoryDraftId}`, {
        params: { draftId },
        signal,
      });
    } catch (e) {
      throwApiError(e);
    }
  }

  async fetchFactTrajectoryCreateDefaults(
    wellId: number,
    draftId: number,
    wellboreId: number,
    signal?: AbortSignal,
  ): Promise<TFactTrajectoryCreateDefaults> {
    try {
      const { data } = await this.agent.get<TFactTrajectoryCreateDefaults>(
        `wells/${wellId}/details/fact/trajectories/create-defaults`,
        {
          params: { draftId, wellboreId },
          signal,
        },
      );

      return data;
    } catch (e) {
      throwApiError(e);
    }
  }

  async calculateCtc(
    wellId: number,
    request: TCalculateCtcRequest,
    signal?: AbortSignal,
    draftId?: number | null,
  ): Promise<TCalculateCtcResponse> {
    try {
      const { data } = await this.agent.post<TCalculateCtcResponse>(
        `wells/${wellId}/details/fact/trajectories/calculate-ctc`,
        request,
        {
          params: {
            ...(hasValue(draftId) ? { draftId } : {}),
          },
          signal,
        },
      );

      return data ?? { rows: [] };
    } catch (e) {
      throwApiError(e);
    }
  }
}
